import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date, formatStr: string = 'dd/MM/yyyy HH:mm'): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, formatStr, { locale: ptBR });
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(d, { addSuffix: true, locale: ptBR });
}

export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }

  const seconds = Math.floor(ms / 1000);

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}m ${remainingSeconds}s`;
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('pt-BR').format(num);
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatPercentage(num: number, decimals: number = 1): string {
  return `${num.toFixed(decimals)}%`;
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    RUNNING: 'bg-blue-100 text-blue-800',
    SUCCESS: 'bg-green-100 text-green-800',
    ERROR: 'bg-red-100 text-red-800',
    TIMEOUT: 'bg-orange-100 text-orange-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
  };

  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getLogLevelColor(level: string): string {
  const colors: Record<string, string> = {
    DEBUG: 'bg-gray-100 text-gray-800',
    INFO: 'bg-blue-100 text-blue-800',
    WARN: 'bg-yellow-100 text-yellow-800',
    ERROR: 'bg-red-100 text-red-800',
    SUCCESS: 'bg-green-100 text-green-800',
  };

  return colors[level] || 'bg-gray-100 text-gray-800';
}

export function truncate(text: string, length: number = 50): string {
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
}

export function parseCron(cron: string): string {
  const patterns: Record<string, string> = {
    '0 9 * * *': 'Todo dia às 9h',
    '0 12 * * *': 'Todo dia às 12h',
    '0 * * * *': 'A cada hora',
    '*/30 * * * *': 'A cada 30 minutos',
    '0 8 * * 1-5': 'Segunda a Sexta às 8h',
  };

  return patterns[cron] || cron;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy:', error);
    return false;
  }
}

export function downloadFile(
  content: string,
  filename: string,
  mimeType: string = 'application/json'
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Formata tempo economizado em minutos para formato legível
 * @param minutes - Tempo em minutos
 * @returns String formatada (ex: "2h 30m" ou "45m")
 */
export function formatTimeSaved(minutes: number): string {
  if (minutes === 0) {
    return '0m';
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}m`;
  }
}

/**
 * Converte minutos em horas decimais
 * @param minutes - Tempo em minutos
 * @returns Tempo em horas (ex: 90 minutos = 1.5 horas)
 */
export function minutesToHours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100;
}

/**
 * Aplica máscara de telefone brasileiro com WhatsApp
 * Formato: +55 (11) 99999-9999
 * @param value - Valor do input
 * @returns String formatada com máscara
 */
export function applyPhoneMask(value: string): string {
  // Remove tudo que não é dígito
  const cleaned = value.replace(/\D/g, '');

  // Limita a 13 dígitos (55 + DDD + 9 dígitos)
  const limited = cleaned.substring(0, 13);

  // Aplica a máscara conforme o tamanho
  if (limited.length <= 2) {
    // Apenas código do país
    return limited.length > 0 ? `+${limited}` : '';
  } else if (limited.length <= 4) {
    // Código do país + parte do DDD
    return `+${limited.substring(0, 2)} (${limited.substring(2)}`;
  } else if (limited.length <= 9) {
    // Código do país + DDD completo + parte do número
    return `+${limited.substring(0, 2)} (${limited.substring(2, 4)}) ${limited.substring(4)}`;
  } else {
    // Número completo
    const ddd = limited.substring(2, 4);
    const firstPart = limited.substring(4, 9);
    const secondPart = limited.substring(9, 13);
    return `+${limited.substring(0, 2)} (${ddd}) ${firstPart}${secondPart ? `-${secondPart}` : ''}`;
  }
}

/**
 * Remove máscara do telefone, retornando apenas dígitos
 * @param value - Telefone formatado
 * @returns Apenas os dígitos (ex: "5511999999999")
 */
export function removePhoneMask(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Valida se o telefone brasileiro está completo
 * @param phone - Telefone (com ou sem máscara)
 * @returns true se o telefone tem 13 dígitos (55 + DDD + 9 dígitos)
 */
export function isValidBrazilianPhone(phone: string): boolean {
  const cleaned = removePhoneMask(phone);
  // Telefone brasileiro: 55 (código) + 2 (DDD) + 9 dígitos
  return cleaned.length === 13 && cleaned.startsWith('55');
}

/**
 * Formata tempo em segundos para MM:SS
 * @param seconds - Tempo em segundos
 * @returns String formatada (ex: "01:30")
 */
export function formatTimer(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}
